import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { LanguageProvider } from "@/lib/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PlanProvider } from "@/context/PlanContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import PublicLayout from "@/components/PublicLayout";
import AppLayout from "@/components/AppLayout";

import Home from "@/pages/public/Home";
import Articles from "@/pages/public/Articles";
import Guide from "@/pages/public/Guide";
import About from "@/pages/public/About";
import Contact from "@/pages/public/Contact";
import Legal from "@/pages/public/Legal";
import Suggestions from "@/pages/public/Suggestions";
import Pricing from "@/pages/public/Pricing";
import ReferralCapture from "@/pages/public/ReferralCapture";
import SuggestionButton from "@/components/SuggestionBox";

import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import Forgot from "@/pages/auth/Forgot";

import Dashboard from "@/pages/user/Dashboard";
import Entry from "@/pages/user/Entry";
import BulkEntry from "@/pages/user/BulkEntry";
import Reports from "@/pages/user/Reports";
import History from "@/pages/user/History";
import MyPlan from "@/pages/user/MyPlan";
import PayQr from "@/pages/user/PayQr";
import ReferEarn from "@/pages/user/ReferEarn";
import Profile from "@/pages/user/Profile";
import MyCalculators from "@/pages/user/MyCalculators";
import CalculatorBuilder from "@/pages/user/CalculatorBuilder";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminRates from "@/pages/admin/AdminRates";
import AdminMenus from "@/pages/admin/AdminMenus";
import AdminContent from "@/pages/admin/AdminContent";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminProfile from "@/pages/admin/AdminProfile";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminReferrals from "@/pages/admin/AdminReferrals";
import AdminRechargeClaims from "@/pages/admin/AdminRechargeClaims";
import AdminQrPayments from "@/pages/admin/AdminQrPayments";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import PrivacyNotice from "@/components/PrivacyNotice";

const ReferralPopup = React.lazy(() => import("@/components/ReferralPopup"));

function RoleHome() {
  const { user } = useAuth();
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;
  return <Navigate to="/app" replace />;
}

export default function App() {
  return (
    <div className="App grain min-h-screen">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <PlanProvider>
            <AnalyticsTracker />
            <PrivacyNotice />
            <React.Suspense fallback={null}><ReferralPopup /></React.Suspense>
            <Routes>
              {/* Public */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/articles" element={<Articles />} />
                <Route path="/guide" element={<Guide />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/suggestions" element={<Suggestions />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/privacy" element={<Legal kind="privacy" />} />
                <Route path="/terms" element={<Legal kind="terms" />} />
                <Route path="/refund" element={<Legal kind="refund" />} />
                <Route path="/disclaimer" element={<Legal kind="disclaimer" />} />
                <Route path="/referral-terms" element={<Legal kind="referral" />} />
                <Route path="/calculator-builder" element={<CalculatorBuilder publicMode />} />
              </Route>

              {/* Referral link */}
              <Route path="/r/:code" element={<ReferralCapture />} />

              {/* Auth */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot" element={<Forgot />} />
              <Route path="/home" element={<RoleHome />} />

              {/* User area */}
              <Route element={<ProtectedRoute role="user" />}>
                <Route element={<AppLayout mode="user" />}>
                  <Route path="/app" element={<Dashboard />} />
                  <Route path="/app/entry" element={<Entry />} />
                  <Route path="/app/bulk" element={<BulkEntry />} />
                  <Route path="/app/reports" element={<Reports />} />
                  <Route path="/app/history" element={<History />} />
                  <Route path="/app/plan" element={<MyPlan />} />
                  <Route path="/pay-qr" element={<PayQr />} />
                  <Route path="/app/refer" element={<ReferEarn />} />
                  <Route path="/app/profile" element={<Profile />} />
                  <Route path="/app/calculators" element={<MyCalculators />} />
                  <Route path="/app/builder" element={<CalculatorBuilder />} />
                  <Route path="/app/builder/:id" element={<CalculatorBuilder />} />
                </Route>
              </Route>

              {/* Admin area */}
              <Route element={<ProtectedRoute role="admin" />}>
                <Route element={<AppLayout mode="admin" />}>
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/users" element={<AdminUsers />} />
                  <Route path="/admin/rates" element={<AdminRates />} />
                  <Route path="/admin/menus" element={<AdminMenus />} />
                  <Route path="/admin/content" element={<AdminContent />} />
                  <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
                  <Route path="/admin/payments" element={<AdminPayments />} />
                  <Route path="/admin/referrals" element={<AdminReferrals />} />
                  <Route path="/admin/recharge-claims" element={<AdminRechargeClaims />} />
                  <Route path="/admin/qr-payments" element={<AdminQrPayments />} />
                  <Route path="/admin/settings" element={<AdminSettings />} />
                  <Route path="/admin/testimonials" element={<AdminTestimonials />} />
                  <Route path="/admin/analytics" element={<AdminAnalytics />} />
                  <Route path="/admin/messages" element={<AdminMessages />} />
                  <Route path="/admin/profile" element={<AdminProfile />} />
                </Route>
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </PlanProvider>
          </BrowserRouter>
          <Toaster position="top-center" richColors closeButton />
          <SuggestionButton />
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}
